import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Seo } from "./components/Seo";
import { Container, SectionHeader, PageHero, FinalCta, CtaButton, Icon, useReveal } from "./components/primitives";
import { SOLUTIONS, SERVICES } from "./content/services";
import { TRANSFORMATION_STORY } from "./content/site";
import { Photo } from "./components/Photo";
import { SOLUTION_PHOTOS, HERO_SETS } from "./content/photos";

export default function Solutions() {
  const ref = useReveal();
  return (
    <div ref={ref}>
      <Seo title="Solutions" description="AI, Digital, Cloud and Data Transformation, Intelligent Automation, Enterprise Software, Workforce Solutions and Technology Modernization — solutions aligned to business outcomes." />
      <PageHero photo={HERO_SETS.solutions} eyebrow="Solutions" title="Transformation programs aligned to the outcomes you need." desc="Solutions combine our services into end-to-end programs — from AI and cloud transformation to intelligent automation and workforce alignment.">
        <CtaButton to="/contact" testId="solutions-hero-cta">Book a Consultation <ArrowRight className="ml-2 h-4 w-4" /></CtaButton>
        <CtaButton to="/services" variant="outline">Explore Services</CtaButton>
      </PageHero>
      <section className="py-24 bg-light">
        <Container>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {SOLUTIONS.map((s, i) => (
              <article key={s.slug} id={s.slug} className="bento light-card reveal p-7 flex flex-col scroll-mt-24" style={{ transitionDelay: `${i * 60}ms` }} data-testid={`solution-${s.slug}`}>
                <div className="card-photo"><Photo name={SOLUTION_PHOTOS[s.slug]} fill parallax={0} sizes="(max-width: 768px) 100vw, 25vw" /></div>
                <div className="relative flex flex-col flex-1">
                <div className="h-12 w-12 rounded-xl bg-[#F0F4FF] text-[#1A6EFF] grid place-items-center"><Icon name={s.icon} className="h-6 w-6" /></div>
                <h2 className="mt-6 font-display text-xl font-bold">{s.title}</h2>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed flex-1">{s.intent}</p>
                {s.services.length > 0 && (
                  <div className="mt-5 flex flex-wrap gap-1.5">{s.services.map((slug) => { const sv = SERVICES.find((x) => x.slug === slug); return <Link key={slug} to={`/services#${slug}`} className="text-[11px] px-2 py-0.5 rounded-full bg-[#F0F4FF] text-[#1A6EFF] hover:bg-[#1A6EFF] hover:text-white transition-colors">{sv.title}</Link>; })}</div>
                )}
                {s.slug === "workforce-transformation" && <Link to="/industries" className="mt-5 text-[11px] px-2 py-0.5 rounded-full bg-[#F0F4FF] text-[#1A6EFF] self-start">Industries served</Link>}
                <Link to="/contact" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1A6EFF]">Discuss this solution <ArrowRight className="h-4 w-4" /></Link>
                </div>
              </article>
            ))}
          </div>
        </Container>
      </section>
      <section className="py-24 bg-navy text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark opacity-30" />
        <Container className="relative">
          <SectionHeader dark eyebrow="The progression" title="Data → Intelligence → Technology → Automation → People → Business Outcomes" align="center" className="reveal" />
          <ol className="mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">{TRANSFORMATION_STORY.map((s, i) => <li key={s.key} className="glass-dark rounded-2xl p-6 reveal" style={{ transitionDelay: `${i * 90}ms` }}><div className="eyebrow text-[#00D4FF]">0{i + 1}</div><h3 className="mt-3 font-display font-bold text-lg">{s.title}</h3><p className="mt-2 text-sm text-white/60">{s.desc}</p></li>)}</ol>
        </Container>
      </section>
      <FinalCta />
    </div>
  );
}
